import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  FssaiGeneratedTag,
  FssaiGeneratedTagService,
} from 'app/service/fssai-generated-tag.service';
import { MasterService } from 'app/service/master.service';
import {
  SUPER_ADMIN_RECIPE_STATUS_OPTIONS,
  SUPER_ADMIN_SHELF_LIFE_UNITS,
  SUPER_ADMIN_TEMPERATURE_UNITS,
  SuperAdminBulkLookups,
  SuperAdminBulkPreviewRow,
  SuperAdminBulkRecipeIngredient,
} from './super-admin-recipe-bulk-upload.models';
import {
  getSuperAdminBulkFieldErrors,
  parseChefMethodProcesses,
  parseProfessionalNotes,
  parseSemicolonList,
  rebuildSuperAdminRecipePayload,
} from './super-admin-recipe-bulk-upload.util';
import {
  BulkIngredientNutrientRow,
  BulkIngredientSearchHit,
  buildBulkFssaiCalculateInput,
  countNonCoreBulkNutrientRows,
  enrichSuperAdminBulkRowsFromIngredients,
  mapBulkIngredientNutrientRows,
  mergeBulkNutrientRows,
  syncBulkClassificationFromIngredients,
} from './super-admin-recipe-bulk-ingredient-enricher';

@Component({
  selector: 'app-super-admin-recipe-bulk-upload-row-editor',
  templateUrl: './super-admin-recipe-bulk-upload-row-editor.component.html',
  styleUrls: ['./super-admin-recipe-bulk-upload-row-editor.component.scss'],
})
export class SuperAdminRecipeBulkUploadRowEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() row!: SuperAdminBulkPreviewRow;
  @Input() lookups!: SuperAdminBulkLookups;

  activeTab = 1;
  geoLoading = false;
  /** Field key → error message (shown in red under fields). */
  fieldErrors: Record<string, string> = {};
  /** Section → error messages for the active tab banner. */
  sectionErrorMessages: string[] = [];
  regionOptions: Array<{ name: string }> = [];
  subRegionOptions: Array<{ id: number; name: string }> = [];
  cuisineGeoTagOptions: Array<{ id: number; name: string }> = [];
  allRegionWiseSubRegions: Array<{ id: number; name: string; region?: string }> = [];

  readonly editorTabs: Array<{ id: number; short: string; label: string }> = [
    { id: 1, short: 'Classification', label: '1. Classification' },
    { id: 2, short: 'Cooking', label: '2. Cooking Process' },
    { id: 3, short: 'Sensory', label: '3. Sensory Profile' },
    { id: 4, short: 'Geo / Cuisine', label: '4. Geo & Cuisine' },
    { id: 5, short: 'Allergens', label: '5. Allergens & Intolerance' },
    { id: 6, short: 'Notes / Chef', label: '6. Notes & Chef Details' },
    { id: 8, short: 'Ingredients', label: '7. Ingredients & UOM' },
    { id: 9, short: 'Storage', label: '8. Storage & Reheat' },
    { id: 11, short: 'Media', label: '9. Media & Costing' },
    { id: 12, short: 'Status', label: '10. Status' },
  ];

  get activeTabLabel(): string {
    return this.editorTabs.find((t) => t.id === this.activeTab)?.label || '';
  }

  get activeTabIndex(): number {
    const idx = this.editorTabs.findIndex((t) => t.id === this.activeTab);
    return idx >= 0 ? idx + 1 : 1;
  }

  readonly statusOptions = SUPER_ADMIN_RECIPE_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly shelfLifeUnits = SUPER_ADMIN_SHELF_LIFE_UNITS;
  readonly temperatureUnits = SUPER_ADMIN_TEMPERATURE_UNITS;
  readonly stateOfProductOptions = [{ value: 'Solid' }, { value: 'Liquid' }];
  readonly ingredientServerSearchFn = (): boolean => true;

  @ViewChild('ingredientDialogTpl') ingredientDialogTpl!: TemplateRef<unknown>;
  @ViewChild('recipeUomDialogTpl') recipeUomDialogTpl!: TemplateRef<unknown>;

  // Ingredient dialog
  ingredientEditIndex: number | null = null;
  ingredientSearchResults: Array<any> = [];
  selectedIngredientId: number | null = null;
  selectedIngredientName = '';
  ingredientServingUnits: Array<any> = [];
  selectedServingUnitId: number | null = null;
  ingredientQuantity: number | null = null;
  ingredientTotalGrams = 0;
  ingredientSearchLoading = false;
  private ingredientSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private ingredientSearchSeq = 0;

  // Recipe UOM
  recipeUomCategories: Array<{ id: number; name: string }> = [];
  recipeUomSubcategories: Array<{ id: number; name: string; ml?: number; gram?: number }> = [];
  selectedUomCategoryId: number | null = null;
  selectedUomSubcategoryId: number | null = null;
  recipeUomLoading = false;

  private ingredientModalRef: NgbModalRef | null = null;
  private recipeUomModalRef: NgbModalRef | null = null;

  // FSSAI tags
  isLoadingGeneratedFssaiTags = false;
  private fssaiTagRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private fssaiGeneratedTagService: FssaiGeneratedTagService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.normalizeSelectionIds();
    void this.bootstrapGeoFromSelections();
    this.scheduleLucideIconRender();
    void this.ensureIngredientsEnriched();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  ngOnDestroy(): void {
    if (this.ingredientSearchTimer) clearTimeout(this.ingredientSearchTimer);
    if (this.fssaiTagRefreshTimer) clearTimeout(this.fssaiTagRefreshTimer);
    try {
      this.ingredientModalRef?.dismiss();
    } catch { /* noop */ }
    try {
      this.recipeUomModalRef?.dismiss();
    } catch { /* noop */ }
  }

  get parsed() {
    return this.row.parsed;
  }

  get selections() {
    return this.row.selections;
  }

  get recipeIngredients(): SuperAdminBulkRecipeIngredient[] {
    return this.selections.recipeIngredients;
  }

  get recipeUoms() {
    return this.selections.recipeUoms;
  }

  get generatedFssaiNutrientTags(): FssaiGeneratedTag[] {
    return this.selections.generatedFssaiNutrientTags as FssaiGeneratedTag[];
  }

  get professionalNotesPreview(): Array<{ title: string; body: string }> {
    return parseProfessionalNotes(this.parsed?.professionalNotes || '');
  }

  get chefMethodPreview(): Array<{ name: string; steps: string[] }> {
    return parseChefMethodProcesses(this.parsed?.chefMethodProcesses || '');
  }

  get chefAuthorNotesList(): string[] {
    return parseSemicolonList(this.parsed?.chefAuthorNotes || '');
  }

  get chefAuthorTipsList(): string[] {
    return parseSemicolonList(this.parsed?.chefAuthorTips || '');
  }

  get hasNotesContent(): boolean {
    return !!(
      String(this.parsed?.professionalNotes || '').trim() ||
      String(this.parsed?.chefMethodProcesses || '').trim() ||
      String(this.parsed?.chefAuthorNotes || '').trim() ||
      String(this.parsed?.chefAuthorTips || '').trim() ||
      String(this.parsed?.remark || '').trim()
    );
  }

  get ingredientTotals() {
    const sumRaw = (pick: (i: SuperAdminBulkRecipeIngredient) => number) =>
      this.recipeIngredients.reduce((sum, i) => sum + (Number(pick(i)) || 0), 0);

    return {
      quantity: this.recipeIngredients.reduce((sum, i) => sum + i.quantity, 0),
      totalGrams: sumRaw((i) => i.totalGrams),
      calories: sumRaw((i) => i.calories),
      protein: sumRaw((i) => i.protein),
      fat: sumRaw((i) => i.fat),
      carbs: sumRaw((i) => i.carbs),
      fiber: sumRaw((i) => i.fiber),
    };
  }

  get preferredRecipeUomIndex(): number {
    const index = this.recipeUoms.findIndex((u) => u.isPreferred);
    return index >= 0 ? index : 0;
  }

  get preferredRecipeUomNutritionObj(): {
    weight: number;
    calories: string;
    carbs: string;
    protein: string;
    fat: string;
    fiber: string;
  } | null {
    if (!this.recipeUoms.length) return null;
    const uom = this.recipeUoms[this.preferredRecipeUomIndex];
    const totals = this.ingredientTotals;
    if (!totals.totalGrams || totals.totalGrams <= 0) return null;

    const ratio = uom.valueGram / totals.totalGrams;
    return {
      weight: uom.valueGram,
      calories: (totals.calories * ratio).toFixed(2),
      carbs: (totals.carbs * ratio).toFixed(2),
      protein: (totals.protein * ratio).toFixed(2),
      fat: (totals.fat * ratio).toFixed(2),
      fiber: (totals.fiber * ratio).toFixed(2),
    };
  }

  setTab(tab: number): void {
    this.activeTab = tab;
    this.refreshSectionErrorBanner();
    this.scheduleLucideIconRender();
  }

  hasFieldError(field: string): boolean {
    return !!this.fieldErrors[field];
  }

  getFieldError(field: string): string {
    return this.fieldErrors[field] || '';
  }

  clearFieldError(field: string): void {
    if (!this.fieldErrors[field]) return;
    delete this.fieldErrors[field];
    this.refreshSectionErrorBanner();
  }

  hasSectionError(section: number): boolean {
    return Object.keys(this.fieldErrors).some((field) => this.fieldSection(field) === section);
  }

  save(): void {
    this.normalizeSelectionIds();
    const errors = getSuperAdminBulkFieldErrors(this.parsed, this.selections);
    if (errors.length) {
      this.fieldErrors = {};
      for (const err of errors) {
        if (!this.fieldErrors[err.field]) {
          this.fieldErrors[err.field] = err.message;
        }
      }
      this.activeTab = errors[0].section;
      this.refreshSectionErrorBanner();
      this.scheduleLucideIconRender();
      this.cdRef.detectChanges();
      // Scroll body to top so errors are visible
      try {
        const body = document.querySelector('.saru-editor__body');
        body?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        /* noop */
      }
      return;
    }

    this.fieldErrors = {};
    this.sectionErrorMessages = [];
    rebuildSuperAdminRecipePayload(this.row, this.lookups, this.allRegionWiseSubRegions);
    this.activeModal.close(this.row);
  }

  private refreshSectionErrorBanner(): void {
    this.sectionErrorMessages = Object.entries(this.fieldErrors)
      .filter(([field]) => this.fieldSection(field) === this.activeTab)
      .map(([, msg]) => msg);
  }

  private fieldSection(field: string): number {
    const map: Record<string, number> = {
      recipeName: 1,
      numberOfServes: 1,
      recipeDescription: 1,
      healthColorCodeId: 1,
      mealTypeIds: 1,
      sourceId: 1,
      cuisineContinentIds: 4,
      occasionIds: 4,
      locationIds: 4,
      seasonIds: 4,
      recipeIngredients: 8,
      storageTypeId: 9,
      shelfLife: 9,
      shelfLifeUnit: 9,
      storageInstructions: 9,
      reheatMethodId: 9,
      reheatTemperature: 9,
      reheatTemperatureUnit: 9,
      reheatingInstructions: 9,
      ccpNotes: 9,
      assignment: 12,
    };
    return map[field] ?? 1;
  }

  /** Ensure multi-select IDs are numbers matching lookup option ids. */
  private normalizeSelectionIds(): void {
    const toNumArr = (ids: any[] | undefined): number[] =>
      (ids || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

    const s = this.selections;
    s.mealTypeIds = toNumArr(s.mealTypeIds);
    s.recipeCategoryIds = toNumArr(s.recipeCategoryIds);
    s.mealPreferenceIds = toNumArr(s.mealPreferenceIds);
    s.foodGroupIds = toNumArr(s.foodGroupIds);
    s.faoFoodGroupIds = toNumArr(s.faoFoodGroupIds);
    s.cookingMethodIds = toNumArr(s.cookingMethodIds);
    s.utensilIds = toNumArr(s.utensilIds);
    s.equipmentIds = toNumArr(s.equipmentIds);
    s.tasteIds = toNumArr(s.tasteIds);
    s.textureIds = toNumArr(s.textureIds);
    s.flavourIds = toNumArr(s.flavourIds);
    s.aromaIds = toNumArr(s.aromaIds);
    s.cuisineContinentIds = toNumArr(s.cuisineContinentIds);
    s.cuisineSubRegionIds = toNumArr(s.cuisineSubRegionIds);
    s.cuisineGeoTagIds = toNumArr(s.cuisineGeoTagIds);
    s.occasionIds = toNumArr(s.occasionIds);
    s.locationIds = toNumArr(s.locationIds);
    s.seasonIds = toNumArr(s.seasonIds);
    s.allergenContainsIds = toNumArr(s.allergenContainsIds);
    s.allergenFreeIds = toNumArr(s.allergenFreeIds);
    s.intoleranceContainsIds = toNumArr(s.intoleranceContainsIds);
    s.intoleranceFreeIds = toNumArr(s.intoleranceFreeIds);

    if (s.dietaryPreferenceId != null && s.dietaryPreferenceId !== ('' as any)) {
      s.dietaryPreferenceId = Number(s.dietaryPreferenceId) || null;
    }
    if (s.sourceId != null && s.sourceId !== ('' as any)) {
      s.sourceId = Number(s.sourceId) || null;
    }
    if (s.storageTypeId != null && s.storageTypeId !== ('' as any)) {
      s.storageTypeId = Number(s.storageTypeId) || null;
    }
    if (s.reheatMethodId != null && s.reheatMethodId !== ('' as any)) {
      s.reheatMethodId = Number(s.reheatMethodId) || null;
    }
    if (s.healthColorCodeId != null) {
      s.healthColorCodeId = String(s.healthColorCodeId);
    }
  }

  formatIngredientMacro(value: number): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0.00';
    const abs = Math.abs(n);
    if (abs === 0) return '0.00';
    if (abs >= 0.01) {
      return (Math.round(n * 100) / 100).toFixed(2);
    }
    const rounded = Math.round(n * 10000) / 10000;
    return rounded.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  getFssaiTagChipClass(tag: FssaiGeneratedTag): string {
    const name = (tag?.fssaiTagName || '').trim().toLowerCase();
    if (name.includes('fibre') || name.includes('fiber') || name.includes('dietary')) {
      return 'fssai-generated-tag-chip--fibre';
    }
    if (name.includes('energy') || name.includes('calorie')) {
      return 'fssai-generated-tag-chip--energy';
    }
    if (name.includes('protein')) return 'fssai-generated-tag-chip--protein';
    if (name.includes('fat') || name.includes('fatty')) return 'fssai-generated-tag-chip--fat';
    if (name.includes('carb')) return 'fssai-generated-tag-chip--carb';
    if (name.includes('sodium') || name.includes('salt')) return 'fssai-generated-tag-chip--sodium';
    if (name.includes('sugar')) return 'fssai-generated-tag-chip--sugar';
    if (name.includes('free')) return 'fssai-generated-tag-chip--free';
    if (name.includes('very low') || name.startsWith('low')) return 'fssai-generated-tag-chip--low';
    if (name.startsWith('high') || name.startsWith('source') || name.startsWith('rich')) {
      return 'fssai-generated-tag-chip--high';
    }
    return 'fssai-generated-tag-chip--default';
  }

  get nutritionBasisLabel(): string {
    const state = String(this.selections.stateOfProduct || '').trim().toLowerCase();
    return state === 'liquid' || state === 'wet' ? 'per 100ml Liquid' : 'per 100g Solid';
  }

  // ── Ingredient dialog ──────────────────────────────────────────────────────

  openIngredientDialog(editIndex?: number): void {
    this.ingredientSearchResults = [];
    this.selectedIngredientId = null;
    this.selectedIngredientName = '';
    this.ingredientServingUnits = [];
    this.selectedServingUnitId = null;
    this.ingredientQuantity = null;
    this.ingredientTotalGrams = 0;

    if (editIndex != null && this.recipeIngredients[editIndex]) {
      this.ingredientEditIndex = editIndex;
      const existing = this.recipeIngredients[editIndex];
      this.selectedIngredientId = existing.ingredientId;
      this.selectedIngredientName = existing.ingredientName;
      this.selectedServingUnitId = existing.servingUnitMasterId;
      this.ingredientQuantity = existing.quantity;
      this.ingredientTotalGrams = existing.totalGrams;
      void this.onIngredientSelected(existing.ingredientId, true);
    } else {
      this.ingredientEditIndex = null;
    }

    this.ingredientModalRef = this.modalService.open(this.ingredientDialogTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      scrollable: true,
      windowClass: 'saru-subdialog-window',
    });
    this.ingredientModalRef.result.catch(() => {
      this.ingredientEditIndex = null;
      this.ingredientModalRef = null;
    });
  }

  closeIngredientDialog(): void {
    this.ingredientModalRef?.dismiss();
    this.ingredientModalRef = null;
    this.ingredientEditIndex = null;
  }

  onIngredientSearch(event: any): void {
    const term = (typeof event === 'string' ? event : event?.term || '').trim();
    if (this.ingredientSearchTimer) {
      clearTimeout(this.ingredientSearchTimer);
      this.ingredientSearchTimer = null;
    }

    if (!term || term.length < 2) {
      this.ingredientSearchSeq += 1;
      this.ingredientSearchResults = [];
      this.ingredientSearchLoading = false;
      this.cdRef.markForCheck();
      return;
    }

    this.ingredientSearchTimer = setTimeout(() => {
      void this.fetchIngredientSearchResults(term);
    }, 300);
  }

  private async fetchIngredientSearchResults(term: string): Promise<void> {
    const seq = ++this.ingredientSearchSeq;
    this.ingredientSearchLoading = true;
    this.cdRef.markForCheck();
    try {
      const res = await this.masterService.searchIngredientsForRecipe(term);
      if (seq !== this.ingredientSearchSeq) return;
      const records = this.extractPgData<any>(res);
      this.ingredientSearchResults = records.map((i: any) => ({
        id: Number(i.id ?? i.Id),
        name: i.ingredientName || i.IngredientName || i.commonName || i.CommonName || 'Unknown',
        calories: i.calories ?? i.Calories ?? 0,
        protein: i.protein ?? i.Protein ?? 0,
        fat: i.fat ?? i.Fat ?? 0,
        carbs: i.carbs ?? i.Carbs ?? 0,
        fiber: i.fiber ?? i.Fiber ?? 0,
        quantity: i.quantity ?? i.Quantity ?? 100,
      }));
    } catch {
      if (seq !== this.ingredientSearchSeq) return;
      this.ingredientSearchResults = [];
    } finally {
      if (seq === this.ingredientSearchSeq) {
        this.ingredientSearchLoading = false;
        this.cdRef.markForCheck();
      }
    }
  }

  async onIngredientSelected(ingredientId: number, isEdit = false): Promise<void> {
    if (!ingredientId) return;
    this.selectedIngredientId = ingredientId;
    if (!isEdit) {
      const found = this.ingredientSearchResults.find((i: any) => i.id === ingredientId);
      this.selectedIngredientName = found?.name || '';
      this.selectedServingUnitId = null;
      this.ingredientTotalGrams = 0;
    }
    try {
      const res = await this.masterService.getIngredientServingUnits(ingredientId);
      this.ingredientServingUnits = res?.data || [];
    } catch {
      this.ingredientServingUnits = [];
    }
    this.calculateTotalGrams();
    this.cdRef.detectChanges();
  }

  onServingUnitChange(): void {
    this.calculateTotalGrams();
  }

  onIngredientQuantityChange(): void {
    this.calculateTotalGrams();
  }

  private calculateTotalGrams(): void {
    if (this.selectedServingUnitId && this.ingredientQuantity && this.ingredientQuantity > 0) {
      const unit = this.ingredientServingUnits.find(
        (u: any) => u.servingUnitMasterId === this.selectedServingUnitId
      );
      if (unit) {
        this.ingredientTotalGrams = Number(unit.valueInGram) * this.ingredientQuantity;
      }
    } else {
      this.ingredientTotalGrams = 0;
    }
  }

  async saveRecipeIngredient(): Promise<void> {
    if (
      !this.selectedIngredientId ||
      !this.selectedServingUnitId ||
      !this.ingredientQuantity ||
      this.ingredientQuantity <= 0
    ) {
      return;
    }

    const unit = this.ingredientServingUnits.find(
      (u: any) => u.servingUnitMasterId === this.selectedServingUnitId
    );
    if (!unit) return;

    const totalGrams = Number(unit.valueInGram) * this.ingredientQuantity;
    const unitLabel =
      unit.displayLabel || `${unit.size || ''} - ${unit.unit} - ${unit.valueInGram} - ${unit.type || ''}`;

    let calories = 0;
    let protein = 0;
    let fat = 0;
    let carbs = 0;
    let fiber = 0;
    const selectedIng = this.ingredientSearchResults.find(
      (i: any) => i.id === this.selectedIngredientId
    );

    if (selectedIng) {
      const baseQty = selectedIng.quantity || 100;
      const ratio = totalGrams / baseQty;
      calories = selectedIng.calories * ratio;
      protein = selectedIng.protein * ratio;
      fat = selectedIng.fat * ratio;
      carbs = selectedIng.carbs * ratio;
      fiber = selectedIng.fiber * ratio;
    } else {
      try {
        const res: any = await this.masterService.getIngredientNutritionParams(
          String(this.selectedIngredientId)
        );
        if (res?.data) {
          const params = Array.isArray(res.data) ? res.data : [];
          for (const p of params) {
            const name = (p.nutrition || '').toLowerCase();
            const val = p.value || 0;
            if (name.includes('calori') || name.includes('energy')) calories = val;
            else if (name === 'protein' || name.includes('protein')) protein = val;
            else if (name === 'fat' || name.includes('total fat')) fat = val;
            else if (name.includes('carb')) carbs = val;
            else if (name.includes('fib')) fiber = val;
          }
          calories = (calories / 100) * totalGrams;
          protein = (protein / 100) * totalGrams;
          fat = (fat / 100) * totalGrams;
          carbs = (carbs / 100) * totalGrams;
          fiber = (fiber / 100) * totalGrams;
        }
      } catch {
        // keep zeros
      }
    }

    const ingredientRow: SuperAdminBulkRecipeIngredient = {
      ingredientId: this.selectedIngredientId,
      ingredientName: this.selectedIngredientName,
      servingUnitMasterId: this.selectedServingUnitId,
      unitLabel,
      approximateValue: Number(unit.valueInGram),
      quantity: this.ingredientQuantity,
      totalGrams,
      calories,
      protein,
      fat,
      carbs,
      fiber,
    };

    if (this.ingredientEditIndex != null) {
      this.selections.recipeIngredients[this.ingredientEditIndex] = ingredientRow;
    } else {
      this.selections.recipeIngredients.push(ingredientRow);
    }
    this.selections.recipeIngredients = [...this.selections.recipeIngredients];
    this.closeIngredientDialog();
    this.clearFieldError('recipeIngredients');
    syncBulkClassificationFromIngredients(this.row, this.lookups);
    this.scheduleGeneratedFssaiTagRefresh();
    this.cdRef.detectChanges();
  }

  deleteRecipeIngredient(index: number): void {
    this.selections.recipeIngredients.splice(index, 1);
    this.selections.recipeIngredients = [...this.selections.recipeIngredients];
    syncBulkClassificationFromIngredients(this.row, this.lookups);
    this.scheduleGeneratedFssaiTagRefresh();
    this.cdRef.detectChanges();
  }

  onStateOfProductChange(): void {
    this.scheduleGeneratedFssaiTagRefresh();
  }

  scheduleGeneratedFssaiTagRefresh(): void {
    if (this.fssaiTagRefreshTimer) clearTimeout(this.fssaiTagRefreshTimer);
    this.fssaiTagRefreshTimer = setTimeout(() => {
      void this.refreshGeneratedFssaiTags();
    }, 400);
  }

  async refreshGeneratedFssaiTags(): Promise<void> {
    if (!this.selections.stateOfProduct) {
      this.selections.generatedFssaiNutrientTags = [];
      return;
    }

    this.isLoadingGeneratedFssaiTags = true;
    this.cdRef.detectChanges();
    try {
      const input = await buildBulkFssaiCalculateInput(this.row, (id) =>
        this.loadIngredientNutrientRows(id)
      );
      if (!input) {
        this.selections.generatedFssaiNutrientTags = [];
        return;
      }

      const hasAny =
        (input.calories ?? 0) > 0 ||
        (input.protein ?? 0) > 0 ||
        (input.fat ?? 0) > 0 ||
        (input.fiber ?? 0) > 0 ||
        (input.carbs ?? 0) > 0 ||
        Object.keys(input.nutrientValues || {}).length > 0;
      if (!hasAny) {
        this.selections.generatedFssaiNutrientTags = [];
        return;
      }

      const res: any = await this.fssaiGeneratedTagService
        .calculateTags({
          stateOfProduct: input.stateOfProduct,
          calories: input.calories,
          protein: input.protein,
          fat: input.fat,
          fiber: input.fiber,
          carbs: input.carbs,
          nutrientValues: input.nutrientValues,
          nutrientUnits: input.nutrientUnits,
        })
        .toPromise();
      let tags = this.fssaiGeneratedTagService.extractTagsFromApiResponse(res);
      if (!tags.length) {
        const payload = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? [];
        tags = this.fssaiGeneratedTagService.normalizeTags(payload);
      }
      this.selections.generatedFssaiNutrientTags = tags;
    } catch {
      this.selections.generatedFssaiNutrientTags = [];
    } finally {
      this.isLoadingGeneratedFssaiTags = false;
      this.cdRef.detectChanges();
    }
  }

  private nutrientRowsCache = new Map<number, BulkIngredientNutrientRow[]>();

  private async loadIngredientNutrientRows(ingredientId: number): Promise<BulkIngredientNutrientRow[]> {
    const id = Number(ingredientId) || 0;
    if (id <= 0) return [];
    if (this.nutrientRowsCache.has(id)) {
      return this.nutrientRowsCache.get(id)!;
    }

    let fromDetail: BulkIngredientNutrientRow[] = [];
    try {
      const res: any = await this.masterService.getIngredientById(String(id), 'RDMS');
      const detail = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
      const nutrientInfo = detail?.nutrientInformation ?? detail?.NutrientInformation ?? [];
      fromDetail = mapBulkIngredientNutrientRows(
        Array.isArray(nutrientInfo) ? nutrientInfo : []
      );
    } catch {
      fromDetail = [];
    }

    // Prefer detail when it already has micronutrients; otherwise merge nutrition-params.
    let rows = fromDetail;
    if (countNonCoreBulkNutrientRows(fromDetail) === 0) {
      try {
        const res: any = await this.masterService.getIngredientNutritionParams(String(id), 'RDMS');
        const payload = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
        const list = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.records ?? payload?.Records ?? payload?.items ?? [];
        const fromParams = mapBulkIngredientNutrientRows(Array.isArray(list) ? list : []);
        rows = mergeBulkNutrientRows(fromDetail, fromParams);
      } catch {
        // keep fromDetail
      }
    }

    this.nutrientRowsCache.set(id, rows);
    return rows;
  }

  private async ensureIngredientsEnriched(): Promise<void> {
    const list = this.selections.recipeIngredients || [];
    const needsWork = list.some((i) => {
      const named = !!String(i.ingredientName || '').trim();
      if (!named) return false;
      const hasMacros =
        Number(i.calories) > 0 ||
        Number(i.protein) > 0 ||
        Number(i.fat) > 0 ||
        Number(i.carbs) > 0;
      const linked = Number(i.ingredientId) > 0 && Number(i.servingUnitMasterId) > 0;
      return !hasMacros || !linked;
    });
    if (!list.length) return;

    if (needsWork) {
      try {
        await enrichSuperAdminBulkRowsFromIngredients([this.row], this.lookups, {
          bulkResolveIngredients: async (names) => this.resolveIngredientNamesBulk(names),
          searchIngredients: async (term) => {
            const res: any = await this.masterService.searchIngredientsForRecipe(term, 1, 10);
            const records = this.extractPgData<any>(res);
            return records.map((i: any) => this.mapIngredientSearchHit(i));
          },
          getServingUnits: async () => [],
        }, undefined, {
          fast: true,
          concurrency: 8,
          calculateFssai: false,
          fetchServingUnits: false,
          fetchDetail: false,
        });
        syncBulkClassificationFromIngredients(this.row, this.lookups);
        this.scheduleGeneratedFssaiTagRefresh();
      } catch (err) {
        console.error('Failed to enrich row ingredients', err);
        this.scheduleGeneratedFssaiTagRefresh();
      }
    } else {
      syncBulkClassificationFromIngredients(this.row, this.lookups);
      this.scheduleGeneratedFssaiTagRefresh();
    }
    this.cdRef.detectChanges();
  }

  private async resolveIngredientNamesBulk(
    names: string[]
  ): Promise<Map<string, BulkIngredientSearchHit | null>> {
    const map = new Map<string, BulkIngredientSearchHit | null>();
    const unique = Array.from(
      new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))
    );
    if (!unique.length) return map;

    const res: any = await this.masterService.resolveIngredientsByNames(unique);
    const payload = res?.data ?? res?.response ?? res?.Response ?? res;
    const matches = Array.isArray(payload?.matches)
      ? payload.matches
      : Array.isArray(payload?.Matches)
        ? payload.Matches
        : [];

    for (const m of matches) {
      const query = String(m?.queryName ?? m?.QueryName ?? '').trim();
      if (!query) continue;
      const key = query.toLowerCase().replace(/\s+/g, ' ');
      const matched = !!(m?.matched ?? m?.Matched);
      if (!matched) {
        map.set(key, null);
        continue;
      }
      map.set(key, this.mapIngredientSearchHit({
        id: m?.id ?? m?.Id ?? m?.ingredientMasterId ?? m?.IngredientMasterId,
        ingredientRefId: m?.ingredientRefId ?? m?.IngredientRefId,
        ingredientName: m?.ingredientName ?? m?.IngredientName,
        commonName: m?.commonName ?? m?.CommonName,
        calories: m?.calories ?? m?.Calories,
        protein: m?.protein ?? m?.Protein,
        fat: m?.fat ?? m?.Fat,
        carbs: m?.carbs ?? m?.Carbs,
        fiber: m?.fiber ?? m?.Fiber,
        quantity: m?.quantity ?? m?.Quantity,
        isVeg: m?.isVeg ?? m?.IsVeg,
        mealPreference: m?.mealPreference ?? m?.MealPreference,
        healthColorCodeId: m?.healthColorCodeId ?? m?.HealthColorCodeId,
        healthColorCodeName: m?.healthColorCodeName ?? m?.HealthColorCodeName,
        colorCode: m?.colorCode ?? m?.ColorCode,
      }));
    }
    return map;
  }

  private mapIngredientSearchHit(i: any): BulkIngredientSearchHit {
    return {
      id: Number(i.id ?? i.Id ?? i.ingredientMasterId ?? i.IngredientMasterId),
      ingredientRefId: Number(i.ingredientRefId ?? i.IngredientRefId ?? 0) || null,
      name: i.ingredientName || i.IngredientName || i.commonName || i.CommonName || 'Unknown',
      calories: Number(i.calories ?? i.Calories ?? 0) || 0,
      protein: Number(i.protein ?? i.Protein ?? 0) || 0,
      fat: Number(i.fat ?? i.Fat ?? 0) || 0,
      carbs: Number(i.carbs ?? i.Carbs ?? 0) || 0,
      fiber: Number(i.fiber ?? i.Fiber ?? 0) || 0,
      quantity: Number(i.quantity ?? i.Quantity ?? 100) || 100,
      isVeg: i.isVeg ?? i.IsVeg ?? null,
      mealPreference: String(i.mealPreference || i.MealPreference || i.dietaryPreference || ''),
      healthColorCodeId: Number(i.healthColorCodeId ?? i.HealthColorCodeId ?? 0) || null,
      healthColorCodeName: String(
        i.healthColorCodeName || i.HealthColorCodeName || i.healthColorCodeLabel || ''
      ),
      colorCode: Number(i.colorCode ?? i.ColorCode ?? 0) || null,
    };
  }

  // ── Recipe UOM ─────────────────────────────────────────────────────────────

  async openRecipeUomDialog(): Promise<void> {
    this.selectedUomCategoryId = null;
    this.selectedUomSubcategoryId = null;
    this.recipeUomSubcategories = [];

    if (!this.recipeUomCategories.length) {
      this.recipeUomLoading = true;
      try {
        const res = await this.masterService.getRecipeUomCategories();
        if (res?.success && res.data) {
          this.recipeUomCategories = res.data.map((c: any) => ({ id: c.id, name: c.name }));
        }
      } catch {
        // ignore
      } finally {
        this.recipeUomLoading = false;
        this.cdRef.detectChanges();
      }
    }

    this.recipeUomModalRef = this.modalService.open(this.recipeUomDialogTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      scrollable: true,
      windowClass: 'saru-subdialog-window saru-subdialog-window--uom',
    });
    this.recipeUomModalRef.result.catch(() => {
      this.recipeUomModalRef = null;
    });
  }

  closeRecipeUomDialog(): void {
    this.recipeUomModalRef?.dismiss();
    this.recipeUomModalRef = null;
    this.selectedUomCategoryId = null;
    this.selectedUomSubcategoryId = null;
    this.recipeUomSubcategories = [];
  }

  async onUomCategoryChange(): Promise<void> {
    this.selectedUomSubcategoryId = null;
    this.recipeUomSubcategories = [];

    if (this.selectedUomCategoryId && this.selectedUomCategoryId > 0) {
      try {
        const res = await this.masterService.getRecipeUomSubcategoriesByCategoryId(
          this.selectedUomCategoryId
        );
        if (res?.success && res.data) {
          this.recipeUomSubcategories = res.data.map((s: any) => ({
            id: s.id,
            name: s.name,
            ml: s.ml || 0,
            gram: s.gram || 0,
          }));
        }
      } catch {
        // ignore
      }
      this.cdRef.detectChanges();
    }
  }

  async addRecipeUomFromDialog(): Promise<void> {
    if (!this.selectedUomCategoryId || !this.selectedUomSubcategoryId) return;

    if (this.recipeUoms.some((u) => u.subcategoryId === this.selectedUomSubcategoryId)) {
      return;
    }

    this.recipeUomLoading = true;
    try {
      const res = await this.masterService.getRecipeUomMasterByCategory(
        this.selectedUomCategoryId,
        this.selectedUomSubcategoryId
      );

      if (res?.success && res.data?.length) {
        const uom = res.data[0];
        this.selections.recipeUoms.push({
          uomId: uom.id,
          categoryId: uom.categoryId,
          categoryName: uom.categoryName || '',
          subcategoryId: uom.subcategoryId,
          subcategoryName: uom.subcategoryName || '',
          ml: uom.ml || 0,
          gram: uom.gram || 0,
          valueGram: uom.gram || 0,
          isPreferred: this.recipeUoms.length === 0,
          name: uom.name,
          abbreviation: uom.abbreviation,
        });
        this.selections.recipeUoms = [...this.selections.recipeUoms];
      }
    } catch {
      // ignore
    } finally {
      this.recipeUomLoading = false;
      this.cdRef.detectChanges();
    }
  }

  onRecipeUomPreferredChange(index: number): void {
    this.recipeUoms.forEach((u, i) => {
      u.isPreferred = i === index;
    });
    this.selections.recipeUoms = [...this.recipeUoms];
    this.cdRef.detectChanges();
  }

  deleteRecipeUomRow(index: number): void {
    this.selections.recipeUoms.splice(index, 1);
    this.selections.recipeUoms = [...this.selections.recipeUoms];
    this.cdRef.detectChanges();
  }

  changeRecipeUomValue(index: number): void {
    if (index < 0 || index >= this.recipeUoms.length) return;
    const changed = this.recipeUoms[index];
    const baseGram = changed.gram;
    if (!baseGram || baseGram <= 0) return;

    const ratio = changed.valueGram / baseGram;
    this.recipeUoms.forEach((u, i) => {
      if (i !== index && u.subcategoryId === changed.subcategoryId) {
        u.valueGram = Number((u.gram * ratio).toFixed(2));
      }
    });
    this.selections.recipeUoms = [...this.recipeUoms];
  }

  saveRecipeUomSelection(): void {
    this.closeRecipeUomDialog();
    this.cdRef.detectChanges();
  }

  // ── Geo cascade ────────────────────────────────────────────────────────────

  async onContinentChange(preserveChildSelections = false): Promise<void> {
    if (!preserveChildSelections) {
      this.selections.cuisineRegionLabels = [];
      this.selections.cuisineSubRegionIds = [];
      this.selections.cuisineGeoTagIds = [];
    }
    this.regionOptions = [];
    this.subRegionOptions = [];
    this.cuisineGeoTagOptions = [];
    this.allRegionWiseSubRegions = [];

    const continentIds = (this.selections.cuisineContinentIds || [])
      .map((id) => Number(id))
      .filter((id) => id > 0);
    this.selections.cuisineContinentIds = continentIds;
    if (!continentIds.length) return;

    this.geoLoading = true;
    try {
      const res = await this.masterService.getRegionWiseCuisinesForCascade(continentIds, []);
      const rows = this.extractPgData<{ id: number; name: string; region?: string }>(res);
      this.allRegionWiseSubRegions = rows.map((r) => ({
        id: Number(r.id),
        name: r.name,
        region: r.region || '',
      }));
      this.regionOptions = this.buildDistinctRegionOptions(this.allRegionWiseSubRegions);
      this.applySubRegionOptionsFromCache();
    } catch (err) {
      console.error('Failed to load geo cascade', err);
    } finally {
      this.geoLoading = false;
      this.cdRef.detectChanges();
    }
  }

  onRegionChange(preserveChildSelections = false): void {
    if (!preserveChildSelections) {
      this.selections.cuisineSubRegionIds = [];
      this.selections.cuisineGeoTagIds = [];
      this.cuisineGeoTagOptions = [];
    }
    this.applySubRegionOptionsFromCache();
  }

  async onSubRegionChange(preserveChildSelections = false): Promise<void> {
    if (!preserveChildSelections) {
      this.selections.cuisineGeoTagIds = [];
      this.cuisineGeoTagOptions = [];
    }
    const subIds = (this.selections.cuisineSubRegionIds || [])
      .map((id) => Number(id))
      .filter((id) => id > 0);
    this.selections.cuisineSubRegionIds = subIds;
    if (!subIds.length) return;

    this.geoLoading = true;
    try {
      const res = await this.masterService.getCuisineSubSubRegionsBySubRegionIds(subIds);
      const rows = this.extractPgData<{ id: number; name: string; cuisineSubRegionId: number }>(res);
      this.cuisineGeoTagOptions = rows.map((r) => ({ id: Number(r.id), name: r.name }));
    } catch (err) {
      console.error('Failed to load cuisine tags', err);
    } finally {
      this.geoLoading = false;
      this.cdRef.detectChanges();
    }
  }

  private async bootstrapGeoFromSelections(): Promise<void> {
    if (!this.selections.cuisineContinentIds?.length) return;

    const savedRegions = [...(this.selections.cuisineRegionLabels || [])];
    const savedSubs = [...(this.selections.cuisineSubRegionIds || [])].map(Number).filter((id) => id > 0);
    const savedTags = [...(this.selections.cuisineGeoTagIds || [])].map(Number).filter((id) => id > 0);

    await this.onContinentChange(true);

    this.selections.cuisineRegionLabels = savedRegions;
    this.applySubRegionOptionsFromCache();
    this.selections.cuisineSubRegionIds = savedSubs;

    if (savedSubs.length) {
      await this.onSubRegionChange(true);
      this.selections.cuisineGeoTagIds = savedTags;
    }

    this.cdRef.detectChanges();
  }

  private applySubRegionOptionsFromCache(): void {
    const labels = new Set(
      (this.selections.cuisineRegionLabels || []).map((l) => l.trim().toLowerCase()).filter(Boolean)
    );
    if (!labels.size) {
      this.subRegionOptions = this.allRegionWiseSubRegions.map((r) => ({
        id: r.id,
        name: r.name,
      }));
      return;
    }
    this.subRegionOptions = this.allRegionWiseSubRegions
      .filter((r) => labels.has((r.region || '').trim().toLowerCase()))
      .map((r) => ({ id: r.id, name: r.name }));
  }

  private buildDistinctRegionOptions(
    rows: Array<{ region?: string }>
  ): Array<{ name: string }> {
    const seen = new Set<string>();
    const options: Array<{ name: string }> = [];
    for (const row of rows) {
      const label = (row.region || '').trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ name: label });
    }
    return options;
  }

  private extractPgData<T>(res: any): T[] {
    const data = res?.data ?? res?.response ?? res?.Response ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.records)) return data.records;
    return [];
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
